// Channel settings — name/topic/privacy/archive. Opened by clicking the
// channel title in ChatHeader (writes the channel id into showChannelSettings).
const _curChannel = (root) => {
  if (!root?.showChannelSettings) return null
  return (root.channels || []).find(c => c.id === root.showChannelSettings) || null
}

// Per-channel draft map keyed by channel id. Avoids the `_id` sentinel that
// would otherwise duplicate `showChannelSettings` as a sync key.
const _draft = (root) => {
  const ch = _curChannel(root)
  if (!ch) return {}
  return root?.channelSettingsDrafts?.[ch.id] || {}
}

const _initDraft = (root) => {
  const ch = _curChannel(root)
  if (!ch) return
  const drafts = root.channelSettingsDrafts || {}
  if (drafts[ch.id]) return
  root.update({
    channelSettingsDrafts: {
      ...drafts,
      [ch.id]: {
        name: ch.name || '',
        topic: ch.topic || '',
        is_private: !!ch.is_private
      }
    }
  })
}

const _patchDraft = (root, patch) => {
  const ch = _curChannel(root)
  if (!ch) return
  const drafts = root.channelSettingsDrafts || {}
  root.update({
    channelSettingsDrafts: {
      ...drafts,
      [ch.id]: { ...(drafts[ch.id] || {}), ...patch }
    }
  })
}

export const ChatChannelSettingsModal = {
  extends: 'Modal',
  stateKey: 'showChannelSettings',

  onUpdate: (el, s) => { _initDraft(s.root) },

  Window: {
    width: 'I',
    maxWidth: '90vw',

    ModalHeader: {
      Hgroup: {
        H: {
          text: (el, s) => {
            const ch = _curChannel(s.root)
            return ch ? `${ch.kind === 'channel' ? '# ' : ''}${ch.name || 'Channel'} settings` : 'Settings'
          }
        },
        P: { text: 'Manage the name, topic, privacy, and members.' }
      },
      Icons: {
        ArchiveBtn: {
          extends: 'IconButton',
          title: (el, s) => _curChannel(s.root)?.is_archived ? 'Unarchive channel' : 'Archive channel',
          Icon: { icon: 'archive' },
          onClick: (e, el, s) => {
            const ch = _curChannel(s.root)
            if (!ch) return
            const willArchive = !ch.is_archived
            s.root.update({
              confirmAction: {
                title: willArchive
                  ? `Archive ${ch.kind === 'channel' ? '#' + (ch.name || 'channel') : 'conversation'}?`
                  : `Unarchive ${ch.kind === 'channel' ? '#' + (ch.name || 'channel') : 'conversation'}?`,
                message: willArchive
                  ? 'Archived channels are hidden from the sidebar. Members can unarchive later.'
                  : 'The channel will appear in the sidebar again.',
                confirmText: willArchive ? 'Archive' : 'Unarchive',
                confirmTheme: willArchive ? 'red' : 'primary',
                icon: 'archive',
                onConfirm: async () => {
                  await el.call('updateChannel', ch.id, { is_archived: willArchive })
                  s.root.update({ showChannelSettings: null })
                }
              }
            })
          }
        }
      }
    },

    Fields: {
      flow: 'y',
      gap: 'B',

      NameField: {
        extends: 'InputField',
        display: (el, s) => _curChannel(s.root)?.kind === 'channel' ? 'flex' : 'none',
        Title: { text: 'Name' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: 'e.g. design-reviews',
          value: (el, s) => _draft(s.root).name || '',
          onInput: (e, el, s) => _patchDraft(s.root, { name: e.target.value })
        }
      },

      TopicField: {
        extends: 'InputField',
        display: (el, s) => _curChannel(s.root)?.kind === 'channel' ? 'flex' : 'none',
        Title: { text: 'Topic' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: 'What is this channel about?',
          value: (el, s) => _draft(s.root).topic || '',
          onInput: (e, el, s) => _patchDraft(s.root, { topic: e.target.value })
        }
      },

      PrivacyRow: {
        display: (el, s) => _curChannel(s.root)?.kind === 'channel' ? 'flex' : 'none',
        flow: 'x',
        align: 'center left',
        gap: 'Z1',
        padding: 'Z 0',

        Box: {
          tag: 'input',
          type: 'checkbox',
          margin: '0',
          cursor: 'pointer',
          checked: (el, s) => !!_draft(s.root).is_private,
          onChange: (e, el, s) => _patchDraft(s.root, { is_private: e.target.checked })
        },
        Label: {
          tag: 'label',
          text: 'Private channel — only invited members can see messages.',
          fontSize: 'Z2',
          color: 'title'
        }
      },

      MuteRow: {
        flow: 'x',
        align: 'center left',
        gap: 'Z1',
        padding: 'Z 0',

        Box: {
          tag: 'input',
          type: 'checkbox',
          margin: '0',
          cursor: 'pointer',
          checked: (el, s) => {
            const ch = _curChannel(s.root)
            if (!ch) return false
            const me = s.root?.currentUserEmail
            const row = (s.root?.channelMembers || []).find(
              (m) => m.channel_id === ch.id && m.member_email === me
            )
            return !!row?.muted
          },
          onChange: async (e, el, s) => {
            const ch = _curChannel(s.root)
            if (!ch) return
            await el.call('setChannelMute', ch.id, e.target.checked)
          }
        },
        Label: {
          tag: 'label',
          text: 'Mute notifications — no bell, dimmed unread badge.',
          fontSize: 'Z2',
          color: 'title'
        }
      },

      LinksSection: {
        flow: 'y',
        gap: 'Z',

        SectionLabel: {
          tag: 'span',
          text: 'Linked event / call',
          fontSize: 'Z2',
          fontWeight: '600',
          color: 'title'
        },

        Current: {
          extends: 'LinkedResources',
          type: 'chat_channel',
          id: (el, s) => _curChannel(s.root)?.id || null
        },

        EventPicker: {
          tag: 'select',
          padding: 'Y Z',
          round: 'Z',
          fontSize: 'Z2',
          background: 'overlay',
          color: 'title',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          cursor: 'pointer',
          onChange: async (e, el, s) => {
            const ch = _curChannel(s.root)
            const evId = e.target.value
            if (!ch || !evId) return
            await el.call('linkResources', 'chat_channel', ch.id, 'calendar_event', evId)
            e.target.value = ''
          },
          childExtends: {
            tag: 'option',
            value: (el, s) => s.value,
            text: (el, s) => s.label
          },
          childrenAs: 'state',
          children: (el, s) => {
            const events = (s.root?.events || []).slice(0, 50)
            return [
              { value: '', label: 'Link to calendar event…' },
              ...events.map((e) => ({ value: String(e.id), label: `${e.title || 'Event'} · ${e.date || ''}` }))
            ]
          }
        }
      },

      MembersSection: {
        flow: 'y',
        gap: 'Z',

        Label: {
          tag: 'span',
          text: 'Members',
          fontSize: 'Z2',
          fontWeight: '600',
          color: 'title'
        },

        List: {
          flow: 'y',
          gap: 'Y',
          paddingLeft: 'Z',
          childExtends: 'ChatSettingsMemberRow',
          childrenAs: 'state',
          children: (el, s) => {
            const ch = _curChannel(s.root)
            if (!ch) return []
            const mems = (s.root.channelMembers || [])
              .filter(m => m.channel_id === ch.id)
            return mems.map(m => ({ email: m.member_email, role: m.role }))
          }
        },

        Empty: {
          display: (el, s) => {
            const ch = _curChannel(s.root)
            if (!ch) return 'none'
            const mems = (s.root.channelMembers || []).filter(m => m.channel_id === ch.id)
            return mems.length ? 'none' : 'flex'
          },
          text: 'No members loaded yet.',
          fontSize: 'Z2',
          color: 'caption'
        }
      }
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z1',
      childExtends: 'Button',

      SaveBtn: {
        theme: 'primary',
        text: 'Save',
        onClick: async (e, el, s) => {
          const ch = _curChannel(s.root)
          if (!ch) return
          const d = _draft(s.root)
          await el.call('updateChannel', ch.id, {
            name: d.name || null,
            topic: d.topic || null,
            is_private: !!d.is_private
          })
          s.root.update({ showChannelSettings: null })
        }
      }
    }
  }
}

export const ChatSettingsMemberRow = {
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  padding: 'Y Z',
  round: 'Z',
  background: 'overlay',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B B',
    src: (el, s) => el.call('avatarUrl', s.email)
  },
  Info: {
    flow: 'y',
    flex: '1',
    minWidth: '0',
    Name: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'title',
      text: (el, s) => {
        const p = el.call('findPerson', s.email)
        return (p && p.name) || s.email || ''
      }
    },
    Role: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => s.role || 'member'
    }
  }
}
